import type { ReactNode } from 'react';
import {
  Copy,
  Flag,
  FolderPlus,
  Forward,
  HeartHandshake,
  Info,
  Link2,
  MessageCircle,
  Pencil,
  Pin,
  PinOff,
  Reply,
  Star,
  Trash2,
} from 'lucide-react';
import { MESSAGE_LIMITS } from '@twinden/shared';
import { attachmentMetadata, firstUrl, type ChatMessage } from './messageUtils';
import type { MessageActions } from './MessageBubble';
import { canBless } from './blessing';
import { t } from '../../i18n';

export interface MessageActionItem {
  key: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  /** Extra text at the end of the row, e.g. "6 of 8". */
  detail?: string;
  danger?: boolean;
}

const EDIT_WINDOW_MS = MESSAGE_LIMITS.EDIT_WINDOW_MINUTES * 60 * 1000;

// The actions for one message, shared by the desktop "…" menu and the phone
// press-and-hold sheet so both always offer the same things (M20/M22 rules).
export function messageActionItems(options: {
  message: ChatMessage;
  isOwn: boolean;
  isGroup: boolean;
  otherParticipantCount: number;
  actions: MessageActions;
  copyText: () => void;
}): MessageActionItem[] {
  const { message, isOwn, isGroup, otherParticipantCount, actions } = options;
  const deleted = Boolean(message.deletedAt);
  const items: MessageActionItem[] = [];

  if (!deleted) {
    items.push({
      key: 'reply',
      label: t('chat.actions.reply'),
      icon: <Reply size={20} />,
      onSelect: () => actions.reply(message),
    });
    // In a group, someone else's message can be answered in their direct
    // chat (D235); not a system line or a deleted account's.
    if (
      isGroup &&
      !isOwn &&
      message.type !== 'system' &&
      message.sender &&
      !message.sender.deleted &&
      actions.replyPrivately
    ) {
      const replyPrivately = actions.replyPrivately;
      items.push({
        key: 'replyPrivately',
        label: t('chat.actions.replyPrivately'),
        icon: <MessageCircle size={20} />,
        onSelect: () => replyPrivately(message),
      });
    }
    // Aashirwad (D280): a blessing in one tap, as a reply.
    if (actions.bless && canBless(message, isOwn)) {
      const bless = actions.bless;
      items.push({
        key: 'bless',
        label: t('chat.bless.action'),
        icon: <HeartHandshake size={20} />,
        onSelect: () => bless(message),
      });
    }
    if (message.type === 'text') {
      items.push({
        key: 'copy',
        label: t('chat.actions.copy'),
        icon: <Copy size={20} />,
        onSelect: options.copyText,
      });
    }
    const canEdit =
      isOwn &&
      message.type === 'text' &&
      Date.now() - new Date(message.createdAt).getTime() < EDIT_WINDOW_MS;
    if (canEdit) {
      items.push({
        key: 'edit',
        label: t('chat.actions.edit'),
        icon: <Pencil size={20} />,
        onSelect: () => actions.edit(message),
      });
    }
    items.push({
      key: 'star',
      label: t(
        message.starredByMe ? 'chat.actions.unstar' : 'chat.actions.star'
      ),
      icon: <Star size={20} />,
      onSelect: () => actions.toggleStar(message),
    });
    if (message.type !== 'system') {
      items.push(
        {
          key: 'pin',
          label: t(
            message.pinnedAt ? 'chat.actions.unpin' : 'chat.actions.pin'
          ),
          icon: message.pinnedAt ? <PinOff size={20} /> : <Pin size={20} />,
          onSelect: () => actions.togglePin(message),
        },
        // A poll's votes and a gathering's replies belong to their chat
        // (D220, D282): not forwarded.
        ...(message.type === 'poll' || message.type === 'gathering'
          ? []
          : [
              {
                key: 'forward',
                label: t('chat.actions.forward'),
                icon: <Forward size={20} />,
                onSelect: () => actions.forward(message),
              },
            ])
      );
    }
    if (firstUrl(message.content)) {
      items.push({
        key: 'link',
        label: t('chat.actions.saveLink'),
        icon: <Link2 size={20} />,
        onSelect: () => actions.saveLink(message),
      });
    }
    if (attachmentMetadata(message)) {
      items.push({
        key: 'document',
        label: t('chat.actions.saveDocument'),
        icon: <FolderPlus size={20} />,
        onSelect: () => actions.saveDocument(message),
      });
    }
    // Message info (M62, D227) on my own messages, groups and direct chats;
    // a group shows how many have seen it.
    if (isOwn && message.type !== 'system') {
      const seen = message.readBy.filter(
        (id) => id !== message.sender?.id
      ).length;
      items.push({
        key: 'info',
        label: t('chat.actions.info'),
        icon: <Info size={20} />,
        ...(isGroup
          ? {
              detail: t('chat.actions.seenCount', {
                n: Math.min(seen, otherParticipantCount),
                total: otherParticipantCount,
              }),
            }
          : {}),
        onSelect: () => actions.showInfo(message),
      });
    }
  }
  // Report someone else's message (M58, D207); system messages aren't anyone's.
  if (!isOwn && !deleted && message.type !== 'system' && message.sender) {
    items.push({
      key: 'report',
      label: t('chat.actions.report'),
      icon: <Flag size={20} />,
      onSelect: () => actions.report(message),
    });
  }
  // Deleted messages keep "Delete for me" (D30); the dialog decides the rest.
  items.push({
    key: 'delete',
    label: t('chat.actions.delete'),
    icon: <Trash2 size={20} />,
    danger: true,
    onSelect: () => actions.remove(message),
  });
  return items;
}
